import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { onDocumentReady } from '@theme/utilities';

/**
 * The header's deferred render comes in two halves, chosen with ?page= (the
 * Liquid reads it through paginate): the desktop mega menu panels, or the
 * phone drawer. Each viewport fetches only the half it shows (~20KB / ~11KB
 * instead of ~24KB, and a third of the work to apply on desktop).
 * `strip` names the other half's keyed targets: its response still carries
 * them, deferred, and applying them would wipe a half already hydrated.
 */
const PARTS = {
  panels: { page: '2', strip: '[data-hydration-key^="header-drawer"], [data-hydration-key^="header-menu-mobile"]' },
  drawer: { page: '3', strip: 'header-menu-categories [data-hydration-key]' },
};

/**
 * Renders in flight, keyed by normalized section id, so an on-demand
 * `hydrateNow` and the idle pass for the same section share one request
 * instead of racing each other into the same DOM.
 * @type {Map<string, Promise<boolean>>}
 */
const inFlight = new Map();

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 * @returns {Promise<boolean>} Whether the section is hydrated once this settles.
 */
function hydrateSection(sectionId, url, part) {
  if (part && PARTS[part]) return hydratePart(sectionId, url, part);

  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section) return Promise.resolve(false);
  if (section.dataset.hydrated === 'true') return Promise.resolve(true);

  const existing = inFlight.get(normalizedId);
  if (existing) return existing;

  const pending = sectionRenderer
    .renderSection(normalizedId, { cache: false, url, mode: 'hydration' })
    .then(() => {
      section.dataset.hydrated = 'true';
      return true;
    })
    .catch(() => false)
    // Cleared either way, so a render that failed (offline, a 5xx) can be
    // retried by the next caller rather than being cached as a dead promise.
    .finally(() => inFlight.delete(normalizedId));

  inFlight.set(normalizedId, pending);
  return pending;
}

/**
 * Hydrate a section immediately rather than waiting for idle, for a caller that
 * has just found out it needs the deferred markup right now -- e.g. a shopper
 * opening a menu panel whose body has not arrived yet. Shares the in-flight
 * render with `hydrate` above, so this never doubles up on the request.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 * @returns {Promise<boolean>} Whether the section is hydrated once this settles.
 */
/**
 * Hydrates one half of the header (see PARTS).
 * @param {string} sectionId
 * @param {URL | undefined} url
 * @param {'panels' | 'drawer'} part
 * @returns {Promise<boolean>}
 */
function hydratePart(sectionId, url, part) {
  const normalizedId = normalizeSectionId(sectionId);
  const selector = buildSectionSelector(normalizedId);
  const section = document.getElementById(selector);
  const flag = part === 'panels' ? 'hydratedPanels' : 'hydratedDrawer';

  if (!section) return Promise.resolve(false);
  if (section.dataset.hydrated === 'true' || section.dataset[flag] === 'true') return Promise.resolve(true);

  const key = `${normalizedId}:${part}`;
  const existing = inFlight.get(key);
  if (existing) return existing;

  const partUrl = new URL(url ?? window.location.href);
  partUrl.searchParams.set('page', PARTS[part].page);

  const pending = sectionRenderer
    .getSectionHTML(normalizedId, false, partUrl)
    .then((html) => {
      const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById(selector);
      if (!fresh) return false;
      fresh.querySelectorAll(PARTS[part].strip).forEach((el) => el.removeAttribute('data-hydration-key'));
      morph(section, fresh, { ...MORPH_OPTIONS, hydrationMode: true });
      section.dataset[flag] = 'true';
      if (section.dataset.hydratedPanels === 'true' && section.dataset.hydratedDrawer === 'true') {
        section.dataset.hydrated = 'true';
      }
      return true;
    })
    .catch(() => false)
    .finally(() => inFlight.delete(key));

  inFlight.set(key, pending);
  return pending;
}

/**
 * @param {string} sectionId
 * @param {URL} [url]
 * @param {'panels' | 'drawer'} [part] - Only that half of the header (see PARTS).
 */
export function hydrateNow(sectionId, url, part) {
  return hydrateSection(sectionId, url, part);
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the DOM is ready.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 * @param {'panels' | 'drawer'} [part] - Only that half of the header (see PARTS).
 */
export async function hydrate(sectionId, url, part) {
  onDocumentReady(() => {
    // The header's menus (desktop panels and mobile drawer) arrive in this pass.
    // It waits for the shopper's first sign of life (pointer, touch, key or
    // scroll) so the large response stays out of the page-load window, with
    // a fallback so it still arrives on a page nobody touches. Opening a menu
    // before then requests the same markup directly (with its spinner).
    //
    // Desktop: bare mouse movement is not intent (the nav's own pointerenter/
    // focusin warms it via hydrateNow), so pointermove is ignored there and the
    // fallback is longer.
    const desktop = window.matchMedia('(min-width: 990px)').matches;
    // Desktop waits for the nav itself (warmed via hydrateNow) or the idle fallback: a
    // click anywhere else on the page - say a variant option - used to start this
    // 270KB parse and morph at the very moment that click's own update was running.
    const events = desktop ? [] : ['pointerdown', 'pointermove', 'touchstart', 'keydown', 'scroll', 'wheel'];
    let started = false;
    let fallback = 0;
    const start = () => {
      if (started) return;
      started = true;
      clearTimeout(fallback);
      events.forEach((type) => window.removeEventListener(type, start));
      hydrateSection(sectionId, url, part);
    };
    events.forEach((type) => window.addEventListener(type, start, { passive: true }));
    fallback = window.setTimeout(
      () => (desktop && 'requestIdleCallback' in window ? window.requestIdleCallback(start, { timeout: 4000 }) : start()),
      desktop ? 6000 : 5000
    );
  });
}
